import { getAverageRgb, getGrayScale, getRgb } from '#base/Color.js';
import { Catch } from '#base/ui/Catch.js';
import { ModalSpinner } from '#base/ui/Spinner.js';
import * as Ajax from '#base/util/ajax.js';
import { useIsFirstRender } from '#base/util/hook.js';
import { DEFAULT_COLOR, DEFAULT_COLOR_DARK, DEFAULT_COLOR_LIGHT, drawPerspective } from '@c2s-npm/plumlib';
import { SVG } from '@svgdotjs/svg.js';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Warp from 'warpjs';

const imgStyle = { maxWidth: '100%', maxHeight: '100%', textAlign: 'center' };

export const Preview = (props) => {
  const { configUrl, designUrl, designData, fallbackImage } = props;
  const [{ data: config, processing, error }, setArgs] = Ajax.useAjax(null);

  const dataUrl = useMemo(() => {
    const blob = new Blob([designData]);
    const url = URL.createObjectURL(blob);
    return url;
  }, [designData]);

  useEffect(() => {
    configUrl && setArgs({ url: configUrl });
  }, [configUrl]);

  const design = designUrl || dataUrl;

  const PlaceHolder = ({ children, muted = false }) => {
    return fallbackImage ? (
      <div style={{ display: 'inline-block', position: 'relative' }}>
        <img src={fallbackImage} style={imgStyle} />
        {muted && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#fff',
              opacity: 0.5,
            }}
          />
        )}
        {children && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              padding: '5px',
              backgroundColor: 'rgba(255,255,255,0.5)',
              textAlign: 'center',
            }}>
            {children}
          </div>
        )}
      </div>
    ) : null;
  };

  if (error || (config && config.error)) {
    return <PlaceHolder>Preview Not Supported</PlaceHolder>;
  } else if (processing) {
    return (
      <PlaceHolder muted={true}>
        <i className='fa fa-fw fa-spinner fa-pulse text-muted' />
      </PlaceHolder>
    );
  } else if (design && config) {
    return (
      <Catch message={() => <PlaceHolder>Error Generating Preview</PlaceHolder>}>
        <PreviewRenderCanvas {...props} config={config} designUrl={design} />
      </Catch>
    );
  } else {
    return <PlaceHolder />;
  }
};
Preview.propTypes = {
  configUrl: PropTypes.string,
  designUrl: PropTypes.string,
  designData: PropTypes.string,
  fallbackImage: PropTypes.string,
  size: PropTypes.oneOf(['original', 'large', 'medium', 'thumb']),
  graphicType: PropTypes.oneOf(['engraved', 'color']),
  zoom: PropTypes.number,
  debug: PropTypes.bool,
};

export class PreviewRender extends React.Component {
  constructor() {
    super();
    this.getSVG = this.getSVG.bind(this);
    this.state = {};
  }

  getSVG() {
    return this.state.raw;
    /*
        const { data, config, graphicType } = this.props;

        const ppmm = 1; // 300;
        const printWidth = config.print.width * ppmm;
        const printHeight = config.print.height * ppmm;

        const svg = SVG().size(printWidth, printHeight);

        svg.svg(this.state.raw);
        const path = svg.path(data);
        const bb = path.bbox();
        if (bb.w / printWidth > bb.h / printHeight) {
            path.size(printWidth, null);
        } else {
            path.size(null, printHeight);
        }
        const { w, h } = path.bbox();
        path.move((printWidth - w) / 2, (printHeight - h) / 2);
        graphicType === 'engraved' && path.fill('#000');

        const result = svg.svg();

        return result;
        */
  }

  render() {
    return <PreviewRenderImpl {...this.props} setRaw={(raw) => this.setState({ raw })} />;
  }
}

export const PreviewRenderImpl = ({ data, config, graphicType, setRaw }) => {
  const renderContainer = useRef(null);
  const scratchRef = useRef(null);
  const previewRef = useRef(null);
  const [scratch, setScratch] = useState(null);
  const [warped, setWarped] = useState([]);
  const [needsUpdate, setUpdate] = useState(0);
  const clear = () => setUpdate(0);
  const update = () => (needsUpdate < 10 ? setUpdate(needsUpdate + 1) : clear());

  const { itemFileUrl, maskFileUrl } = config || {};

  const [viewWidth, setViewWidth] = useState(0);
  const [viewHeight, setViewHeight] = useState(0);
  const imageLoaded = (e) => {
    const { offsetWidth, offsetHeight } = e.target;

    offsetWidth > viewWidth && setViewWidth(offsetWidth);
    offsetHeight > viewHeight && setViewHeight(offsetHeight);
  };

  useEffect(() => {
    setScratch(SVG(scratchRef.current));
    update();
  }, []);

  const containerWidth = renderContainer.current ? renderContainer.current.offsetWidth : 500;
  const containerHeight = viewWidth ? (containerWidth / viewWidth) * viewHeight : 500;

  useEffect(() => {
    if (!needsUpdate) return;

    if (scratch && config) {
      scratch.clear();
      if (!data) {
        clear();
        return;
      }

      const ppmm = config.preview.height / config.item.height;
      const printWidth = config.print.width * ppmm;
      const printHeight = config.print.height * ppmm;
      scratch.size(printWidth, printHeight);
      let styles = [];
      const loadData = () => {
        const isSvg = data && data.match(/</);
        if (!isSvg) {
          const group = scratch.group();
          const path = group.path(data);
          path.move(0, 0);
          return group;
        } else {
          // pass provided art through warpjs to convert everything to paths
          scratch.group().svg(data);

          new Warp(scratchRef.current);

          const paths = Array.from(scratchRef.current.querySelectorAll('path'));
          styles = Array.from(scratchRef.current.querySelectorAll('style'));
          scratch.clear();
          const group = scratch.group();
          paths.forEach((path) => {
            group.path(path.getAttribute('d')).attr('class', path.getAttribute('class'));
          });
          group.move(0, 0);

          return group;
        }
      };
      const path = loadData();
      const bb = path.rbox();
      const scale = bb.w / printWidth > bb.h / printHeight ? printWidth / bb.w : printHeight / bb.h;
      path.each(function () {
        const { w, h, x, y } = this.rbox();
        this.size(w * scale, h * scale);
        this.move(x * scale, y * scale);
      });
      const { w, h } = path.rbox();
      path.move((printWidth - w) / 2, (printHeight - h) / 2);
      scratchRef.current.prepend(...styles);
      setRaw(scratch.svg());
      scratchRef.current.querySelectorAll('style').forEach((e) => e.remove());
      path.move(-w / 2, 0);

      const warp = new Warp(scratchRef.current);
      warp.interpolate(4);
      const ellipse = { height: config.preview.perspective, width: config.preview.width };

      warp.transform(([x, y]) => {
        const offset = Math.sqrt(
          Math.pow(ellipse.height / 2, 2) * (1 - Math.pow(x, 2) / Math.pow(ellipse.width / 2, 2))
        );
        return [x, y + offset];
      });

      // reload the svg
      const svg = scratchRef.current.innerHTML;
      scratch.clear();
      scratch.svg(svg);

      scratch.each(function () {
        this.move(0, 0);
      });

      scratch.each(function () {
        const { width, height } = this.bbox();
        const perspectiveShift = (height - h) / 2;
        this.move(
          viewWidth / 2 - width / 2,
          config.print.offsetTop * ppmm +
            config.preview.offsetTop -
            perspectiveShift +
            printHeight / 2 -
            height / 2
        );
      });

      const paths = Array.from(scratchRef.current.querySelectorAll('path'));
      const warped = paths.map((warped) => {
        return { d: warped.getAttribute('d'), className: warped.getAttribute('class') };
      });
      setWarped(warped);

      previewRef.current.querySelectorAll('style').forEach((e) => e.remove());
      previewRef.current.prepend(...styles);

      clear();
    } else {
      update();
    }
  }, [needsUpdate]);

  useEffect(() => {
    update();
  }, [data, viewWidth, viewHeight, config, graphicType]);

  return (
    <div ref={renderContainer}>
      <div style={{ visibility: 'hidden', position: 'absolute', zIndex: -999 }}>
        <svg ref={scratchRef}></svg>
        <img src={itemFileUrl} onLoad={imageLoaded} />
        <img src={maskFileUrl} onLoad={imageLoaded} />
      </div>
      <svg
        ref={previewRef}
        width={containerWidth}
        height={containerHeight}
        viewBox={`0 0 ${viewWidth} ${viewHeight}`}>
        {graphicType === 'engraved' && (
          <defs>
            <clipPath id='mask'>
              {warped.map((w, i) => (
                <path key={i} d={w.d}></path>
              ))}
            </clipPath>
          </defs>
        )}
        <image href={itemFileUrl} width={viewWidth} height={viewHeight}></image>
        {/*<path d={warped} stroke="#000" stroke-width="1" stroke-opacity="0.1"></path>*/}
        {graphicType === 'engraved' && (
          <image href={maskFileUrl} width={viewWidth} height={viewHeight} clipPath='url("#mask")'></image>
        )}
        {graphicType === 'color' && warped.map((w, i) => <path key={i} {...w}></path>)}
      </svg>
    </div>
  );
};

const _ImageSizes = {
  large: 1000,
  medium: 450,
  thumb: 105,
};

const DebugStrokes = {
  original: 16,
  large: 10,
  medium: 4,
  thumb: 2,
};

export const PreviewRenderCanvas = React.forwardRef(
  (
    {
      designUrl,
      config,
      graphicType,
      size = 'medium',
      colorize = false,
      debug = false,
      zoom = 0.8,
      onRenderingChange,
      onSetPreview,
      perspective: { smoothness = 5, divisions = 5 } = {},
    },
    forwardRef
  ) => {
    const isInitializing = useIsFirstRender();

    const debounce = useRef(null);
    const cancelUpdate = useRef(null);

    // canvases
    const scratchRef = useRef(null);
    const compRef = useRef(null);

    // source images
    const itemRef = useRef(null);
    const maskRef = useRef(null);
    const designRef = useRef(null);

    // target image
    // const outputRef = useRef(null);
    React.useImperativeHandle(forwardRef, () => ({
      export: () => {
        // TODO
      },
      getSVG: () => {
        return designRef.current?.src;
      },
    }));

    const { preview: { previewFile, maskFile } = {} } = config || {};
    const itemFileUrl = previewFile ? previewFile.sizes[size] : config.itemFileUrl;
    const maskFileUrl = maskFile ? maskFile.sizes[size] : config.maskFileUrl;

    // final image url/data
    const [preview, _setPreview] = useState(itemFileUrl);
    const setPreview = (e) => {
      _setPreview(e);
      onSetPreview && onSetPreview(e);
    };
    // currently rendering
    const [rendering, _setRendering] = useState(false);
    const setRendering = (v) => {
      _setRendering(v);
      onRenderingChange && onRenderingChange(v);
    };

    // state of loading source images
    const [isItemLoaded, setItemLoaded] = useState(false);
    const [isMaskLoaded, setMaskLoaded] = useState(!maskFileUrl);
    const [isDesignLoaded, setDesignLoaded] = useState(false);
    const onMaskLoaded = () => setMaskLoaded(true);
    const onItemLoaded = () => setItemLoaded(true);
    const onDesignLoaded = () => setDesignLoaded(true);
    useEffect(() => {
      !isInitializing && setItemLoaded(false);
    }, [itemFileUrl]);
    useEffect(() => {
      if (!maskFileUrl) {
        setMaskLoaded(true);
      } else if (!isInitializing) {
        setMaskLoaded(false);
      }
    }, [maskFileUrl]);
    useEffect(() => {
      !isInitializing && setDesignLoaded(false);
    }, [designUrl]);
    useEffect(() => {
      if (!isDesignLoaded && !!designRef.current?.complete) {
        !isInitializing && setDesignLoaded(true);
      }
    });

    const defaultColor = useMemo(() => {
      const colors = (config?.item?.color || []).filter((c) => !!c);
      if (graphicType === 'color' && colors.length > 0) {
        const rgb = colors.map(getRgb);
        const avg = getAverageRgb(rgb);
        const grey = getGrayScale(avg);
        const color = grey >= 128 ? DEFAULT_COLOR_DARK : DEFAULT_COLOR_LIGHT;
        return color;
      } else {
        return DEFAULT_COLOR;
      }
    }, [config]);

    const isLoaded = isDesignLoaded && isMaskLoaded && isItemLoaded;

    const viewWidth = itemRef.current?.naturalWidth || 0;
    const viewHeight = itemRef.current?.naturalHeight || 0;

    // width is probably more accurately measured, so use that to calc pixels per millimeter
    const ppmm = config.preview.width / config.item.width;
    const printWidth =
      (config.print.width > config.item.width ? config.item.width : config.print.width) * ppmm;
    const printHeight = config.print.height * ppmm;

    const previewHeight = viewHeight; // ImageSizes[size] || viewHeight;
    const previewWidth = viewWidth; //ImageSizes[size] || viewWidth;

    const debugStroke = DebugStrokes[size] || 2;
    const debugColor = 'rgba(255,0,255,0.5)';

    const sourceHeight = config.preview?.previewFile.height || previewHeight;
    const sourceWidth = config.preview?.previewFile.width || previewWidth;
    const previewScale =
      sourceHeight / sourceWidth > previewHeight / previewWidth
        ? previewHeight / sourceHeight
        : previewWidth / sourceWidth;

    // set the preview to the item image until the preview can be rendered
    useEffect(() => {
      if (config.preview.previewFile) {
        setPreview(config.preview.previewFile.sizes[size]);
      }
    }, [config]);

    // do the preview render
    useEffect(() => {
      const update = async () => {
        if (!isLoaded) return;

        const { naturalWidth: w, naturalHeight: h } = designRef.current;

        const scale = w / printWidth > h / printHeight ? printWidth / w : printHeight / h;

        const comp = compRef.current.getContext('2d');
        comp.imageSmoothingQuality = 'high';
        comp.imageSmoothingEnabled = true;

        const scratch = scratchRef.current.getContext('2d');
        scratch.imageSmoothingQuality = 'high';
        scratch.imageSmoothingEnabled = true;

        const loadImage = (src) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              resolve(img);
            };
            img.src = src;
          });
        };

        const scaleDesign = async () => {
          scratchRef.current.width = printWidth;
          scratchRef.current.height = printHeight;
          scratch.clearRect(0, 0, printWidth, printHeight);

          const width = w * scale * zoom;
          const height = h * scale * zoom;
          const top = (printWidth - width) / 2;
          const left = (printHeight - height) / 2;

          scratch.drawImage(designRef.current, top, left, width, height);
          if (colorize) {
            scratch.save();
            scratch.globalCompositeOperation = 'source-in';
            scratch.rect(0, 0, printWidth, printHeight);
            scratch.fillStyle = defaultColor;
            scratch.fill();
            scratch.restore();
          }

          return await loadImage(scratchRef.current.toDataURL());
        };

        const applyCurve = (imageWidth, imageHeight, perspectiveWidth, perspectiveHeight) => {
          const perspective = Math.abs(perspectiveHeight || 0);

          const targetWidth = imageWidth;
          const targetHeight = imageHeight + perspective;

          if (perspectiveHeight) {
            var imgData = scratch.getImageData(0, 0, targetWidth, targetHeight);
            scratch.clearRect(0, 0, targetWidth, targetHeight);
            var warped = scratch.createImageData(targetWidth, targetHeight);

            const h2 = Math.pow(perspectiveHeight / 2, 2);
            const w2 = Math.pow(perspectiveWidth / 2, 2);

            const translate = (xIn, yIn) => {
              const x = xIn - targetWidth / 2;

              const offset = Math.sqrt(h2 * (1 - Math.pow(x, 2) / w2));
              const yOut = yIn + (perspectiveHeight > 0 ? offset : perspective / 2 - offset);

              return [xIn, yOut];
            };

            const xl = imgData.width;
            const yl = imgData.height;
            for (var x = 0; x < xl; x++) {
              for (var y = 0; y < yl; y++) {
                const from = (y * xl + x) * 4;

                // only translate pixels that are visible
                const alpha = imgData.data[from + 3];

                if (alpha) {
                  const [dx, dy] = translate(x, y);
                  const to = (parseInt(dy) * xl + parseInt(dx)) * 4;

                  // translate rgba
                  warped.data[to] = imgData.data[from];
                  warped.data[to + 1] = imgData.data[from + 1];
                  warped.data[to + 2] = imgData.data[from + 2];
                  warped.data[to + 3] = alpha;
                }
              }
            }

            scratch.putImageData(warped, 0, 0);
          }
        };

        const applyPerspective = async (image) => {
          scratchRef.current.width = printWidth;
          scratchRef.current.height = printHeight + Math.abs(config.preview.perspective || 0);
          scratch.clearRect(0, 0, scratchRef.current.width, scratchRef.current.height);
          scratch.drawImage(image, 0, 0);

          applyCurve(printWidth, printHeight, config.preview.width, config.preview.perspective);

          const warped = await loadImage(scratchRef.current.toDataURL());
          // const scaledX = (viewWidth - (printWidth * previewScale)) / 2;

          const targetWidth = scratchRef.current.width;
          const targetHeight = scratchRef.current.height;

          const itemWidth = config.preview.width;
          const itemLeft = config.preview.offsetLeft
            ? config.preview.offsetLeft
            : (viewWidth / previewScale - itemWidth) / 2;
          const designLeft = config.print.offsetLeft
            ? config.print.offsetLeft * ppmm
            : (itemWidth - printWidth) / 2;
          const scaledX = (itemLeft + designLeft) * previewScale;

          const scaledY =
            (config.preview.offsetTop +
              config.print.offsetTop * ppmm -
              (config.preview.perspective > 0 ? Math.abs(config.preview.perspective) / 2 : 0)) *
            previewScale;
          const scaledWidth = targetWidth * previewScale;
          const scaledHeight = targetHeight * previewScale;

          // draw warped design scaled to preview size
          comp.drawImage(
            warped,
            0,
            0,
            targetWidth,
            targetHeight,
            scaledX,
            scaledY,
            scaledWidth,
            scaledHeight
          );

          return warped;
        };

        const applyCorners = async (image) => {
          if (config.preview.corners) {
            debug && console.time(config.itemId + '.PreviewRenderCanvas.applyCorners');
            const itemWidth = config.item.width * ppmm;
            const itemHeight = config.item.height * ppmm;
            scratchRef.current.width = itemWidth;
            scratchRef.current.height = itemHeight;
            scratch.clearRect(0, 0, scratchRef.current.width, scratchRef.current.height);
            const left = config.print.offsetLeft
              ? config.print.offsetLeft * ppmm
              : (itemWidth - printWidth) / 2;
            const top = (config.print.offsetTop || 0) * ppmm;
            scratch.drawImage(image, left, top);

            const matted = await loadImage(scratchRef.current.toDataURL());

            scratchRef.current.width = config.preview.previewFile.width;
            scratchRef.current.height = config.preview.previewFile.height;
            scratch.clearRect(0, 0, scratchRef.current.width, scratchRef.current.height);

            debug && console.log('PreviewRenderCanvas.applyCorners', { divisions, smoothness });
            drawPerspective(
              scratch, // draw to
              matted,
              0,
              0,
              itemWidth,
              itemHeight, // sample from
              config.preview.corners.map((c) => c[0]), // x
              config.preview.corners.map((c) => c[1]), // y
              divisions || 5, // divisions
              smoothness || 5, // smoothness
            );

            const warped = await loadImage(scratchRef.current.toDataURL());
            comp.drawImage(warped, 0, 0, viewWidth, viewHeight);

            debug && console.timeEnd(config.itemId + '.PreviewRenderCanvas.applyCorners');
            return warped;
          } else {
            return null;
          }
        };

        const applyMask = () => {
          // mask the design if appropriate
          if (graphicType === 'engraved') {
            comp.save();
            comp.globalCompositeOperation = 'source-in';
            // TODO decide on centering:
            // comp.drawImage(maskRef.current,(viewWidth - maskRef.current.naturalWidth) / 2,0);
            // OR scaling to item width
            comp.drawImage(
              maskRef.current,
              (viewWidth - config.preview.width * previewScale) / 2,
              0,
              config.preview.width * previewScale,
              maskRef.current.naturalHeight
            );
            comp.restore();
          } else {
            // Add some sheen to the image
            /*
                    const original = new Image();
                    original.onload = () => {
                        comp.globalCompositeOperation = 'source-in';
                        comp.drawImage(maskRef.current,(viewWidth - maskRef.current.naturalWidth) / 2,0);
                        const overlay = new Image();
                        overlay.onload = () => {
                            comp.globalCompositeOperation = 'source-in';
                            comp.drawImage(original, 0, 0);
                            comp.globalCompositeOperation = 'overlay';
                            comp.drawImage(overlay, 0, 0);
                        }
                        overlay.src = compRef.current.toDataURL();

                    }
                    original.src = compRef.current.toDataURL();
                    */
          }
        };

        const applyProduct = () => {
          comp.save();
          comp.globalCompositeOperation = 'destination-atop';
          comp.drawImage(itemRef.current, (viewWidth - itemRef.current.naturalWidth) / 2, 0);
          comp.restore();
        };

        const applyDebug = () => {
          try {
            const itemWidth = config.preview.width || 0;
            const itemHeight = config.preview.height || 0;
            const itemLeft = config.preview.offsetLeft
              ? config.preview.offsetLeft
              : (viewWidth / previewScale - itemWidth) / 2;
            const itemTop = config.preview.offsetTop || 0;

            const designLeft = config.print.offsetLeft
              ? config.print.offsetLeft * ppmm
              : (itemWidth - printWidth) / 2;
            const designTop = (config.print.offsetTop || 0) * ppmm;

            comp.strokeStyle = debugColor;
            comp.lineWidth = debugStroke;

            // product bounds
            console.log('Drawing debug product bounds', config.itemId, size, {
              itemWidth,
              itemHeight,
              itemLeft,
              itemTop,
              designLeft,
              designTop,
            });
            comp.strokeRect(
              itemLeft * previewScale + debugStroke / 2,
              itemTop * previewScale + debugStroke / 2,
              itemWidth * previewScale - debugStroke,
              itemHeight * previewScale - debugStroke
            );

            if (config.preview.corners) {
              console.log('Drawing debug corners', config.itemId, size);

              scratchRef.current.width = itemWidth * previewScale;
              scratchRef.current.height = itemHeight * previewScale;

              scratch.strokeStyle = debugColor;
              scratch.lineWidth = debugStroke;

              scratch.strokeRect(
                debugStroke / 2,
                debugStroke / 2,
                itemWidth * previewScale - debugStroke,
                itemHeight * previewScale - debugStroke
              );
              scratch.strokeRect(
                designLeft * previewScale + debugStroke / 2,
                designTop * previewScale + debugStroke / 2,
                printWidth * previewScale - debugStroke,
                printHeight * previewScale - debugStroke
              );

              itemWidth &&
                itemHeight &&
                drawPerspective(
                  comp, // draw to
                  scratchRef.current,
                  0,
                  0,
                  itemWidth * previewScale,
                  itemHeight * previewScale, // sample from
                  config.preview.corners.map((c) => c[0] * previewScale), // x
                  config.preview.corners.map((c) => c[1] * previewScale), // y
                  5, // divisions
                  5, // smoothness
                );
            }

            console.log('Drawing debug print bounds', config.itemId, size);
            // print bounds
            scratchRef.current.width = printWidth * previewScale;
            scratchRef.current.height =
              (printHeight + Math.abs(config.preview.perspective || 0)) * previewScale;
            scratch.clearRect(0, 0, printWidth, printHeight);

            scratch.strokeStyle = debugColor;
            scratch.lineWidth = debugStroke;

            scratch.strokeRect(
              debugStroke / 2,
              debugStroke / 2,
              printWidth * previewScale - debugStroke,
              printHeight * previewScale - debugStroke
            );
            applyCurve(
              printWidth * previewScale,
              printHeight * previewScale,
              config.preview.width * previewScale,
              config.preview.perspective * previewScale
            );
            comp.drawImage(
              scratchRef.current,
              (itemLeft + designLeft) * previewScale,
              (itemTop +
                designTop -
                (config.preview.perspective > 0 ? Math.abs(config.preview.perspective || 0) / 2 : 0)) *
                previewScale
            );
          } catch (e) {
            console.long('Error drawing debug bounds', config, size, e);
          }
        };

        const doComp = () => {
          let cancel = false;

          setRendering(true);
          setTimeout(async () => {
            debug && console.time(config.itemId + '.PreviewRenderCanvas.doComp');
            try {
              const scaled = await scaleDesign();
              comp.clearRect(0, 0, viewWidth, viewHeight);
              await applyCorners(scaled);
              await applyPerspective(scaled);
              applyMask();
              applyProduct();
              debug && applyDebug();

              setRendering(false);
              setPreview(compRef.current.toDataURL('image/png'));
            } catch (e) {
              console.error(e);
              !cancel && setRendering(false);
            }
            debug && console.timeEnd(config.itemId + '.PreviewRenderCanvas.doComp');
          }, 0);

          return () => {
            console.log('canceling render update!');
            cancel = true;
            setRendering(false);
          };
        };

        cancelUpdate.current && cancelUpdate.current();
        cancelUpdate.current = doComp();
      };

      debounce.current && clearTimeout(debounce.current);
      debounce.current = setTimeout(update, 10);
    }, [isLoaded, graphicType, config]);

    // TODO do all the processing without DOM placeholders
    return (
      <div style={{ display: 'inline-block', position: 'relative' }}>
        <div style={{ visibility: 'hidden', position: 'absolute', zIndex: -9999, display: 'none' }}>
          <img ref={itemRef} src={itemFileUrl} onLoad={onItemLoaded} crossOrigin='anonymous' />
          <img ref={maskRef} src={maskFileUrl} onLoad={onMaskLoaded} crossOrigin='anonymous' />
          <img
            ref={designRef}
            height={printHeight}
            src={designUrl}
            onLoad={onDesignLoaded}
            crossOrigin='anonymous'
          />
          <canvas
            ref={scratchRef}
            width={config.preview.previewFile.width}
            height={config.preview.previewFile.height}
          />
          <canvas ref={compRef} width={viewWidth} height={viewHeight} />
        </div>
        {preview && <img ref={forwardRef} src={preview} />}
        {rendering && <ModalSpinner />}
      </div>
    );
  }
);
PreviewRenderCanvas.propTypes = {
  size: PropTypes.oneOf(['original', 'large', 'medium', 'thumb']),
  graphicType: PropTypes.oneOf(['engraved', 'color']),
  zoom: PropTypes.number,
  debug: PropTypes.bool,
};

// TODO generate an SVG given a design config
/*
const Config = () => {
  const [state, useState] = useState(
    fromJS({
      sides: [],
      version: 0.1,
    })
  );
};
*/
